import { cloneDeep } from 'lodash';

import { DEFAULT_BANKS_BY_DEAL_TYPE, DOC_FEE, FinancialInfo } from '../gql/financialInfoGql';
import { Bank, DealType, Maybe, TtNewBankEnum } from '../gql/generated/graphql';

import { LienholderNameEnum } from '../constants/lienholders';
import { STATES_WITH_NO_DOC_FEE } from '../libs/states';

import { isNullOrUndefined } from '.';

export const MAX_TERM = 120;

// A term we can work out a payment from. The API reads 0 as 1, so sending a cleared box
// would preview a one-month loan.
export const isSellableTerm = (term?: Maybe<number>): boolean =>
  term != null && Number.isInteger(term) && term >= 1 && term <= MAX_TERM;

export const cleanFinancialInfo = (financialInfo?: FinancialInfo) => {
  if (!financialInfo) {
    return financialInfo;
  }

  const financialInfoCopy = cloneDeep(financialInfo);

  delete financialInfoCopy.newBaseTaxAmount;
  delete financialInfoCopy.totalTaxAmount;
  delete financialInfoCopy.feesQueryFailed;
  delete financialInfoCopy.maxMarkup;
  delete financialInfoCopy.minMarkup;
  delete financialInfoCopy.tempUserEnteredReserve;
  delete financialInfoCopy.newBuyRate;

  delete financialInfoCopy.selected_credit_decision;

  // Older deals can hold a term we could never sell. Sending it back makes the API refuse
  // the whole save, losing the user's other edits with it.
  if (!isSellableTerm(financialInfoCopy.term)) {
    delete financialInfoCopy.term;
  }

  return financialInfoCopy;
};

const NON_R1_BANKS = [
  TtNewBankEnum.Afcu,
  TtNewBankEnum.Iccu,
  TtNewBankEnum.Lookout,
  TtNewBankEnum.MountainAmerica,
  TtNewBankEnum.Occu,
];
export const ZERO_MARKUP_BANKS = [
  TtNewBankEnum.Lookout,
  TtNewBankEnum.Afcu,
  TtNewBankEnum.FirstInterstate,
  TtNewBankEnum.Wings,
  TtNewBankEnum.MountainAmerica,
  TtNewBankEnum.GlobalLendingServices,
  TtNewBankEnum.FifthThird,
];

export const findBankSlug = ({
  banks,
  bankName,
}: {
  banks: Bank[];
  bankName: Maybe<string> | undefined;
}): Maybe<TtNewBankEnum> | undefined => banks.find((bank) => bank.name === bankName)?.slug;

export const findBankName = ({
  banks,
  bankSlug,
}: {
  banks: Bank[];
  bankSlug: TtNewBankEnum;
}): Maybe<string> | undefined => banks.find((bank) => bank.slug === bankSlug)?.name;

export const canBankUseR1 = (bankSlug: Maybe<TtNewBankEnum> | undefined): boolean =>
  bankSlug != null && !NON_R1_BANKS.includes(bankSlug);

type MaxMarkupArgs = {
  banks: Bank[];
  bankName?: string;
  term?: number;
};

export const getMaxBankMarkup = ({ banks, bankName, term }: MaxMarkupArgs) => {
  if (banks.length === 0 || !bankName || !term) {
    return null;
  }

  const bank = banks.find((b) => b.name === bankName);
  const bankMarkup = bank?.bank_markups?.find(
    (m) => (m?.term_min ?? 0) <= term && (m?.term_max ?? 0) >= term,
  );

  return bankMarkup?.max_markup ?? null;
};

type MinMarkupArgs = {
  bankSlug: Maybe<TtNewBankEnum> | undefined;
  maxMarkup: Maybe<number>;
  stateDocFeeFlag: boolean;
  state: string;
};

export const getMinBankMarkup = ({
  bankSlug,
  state,
  stateDocFeeFlag,
  maxMarkup,
}: MinMarkupArgs): number | null => {
  if (isNullOrUndefined(maxMarkup)) {
    return null;
  }

  // Apply in states with no doc fee if the bank allows markups.
  // We also need to check the `maxMarkup` is at least 1 to avoid creating an impossible scenario where the `minMarkup` is greater than the `maxMarkup`.
  // And Fifth Third allows negative markups.
  const canApplyMinMarkup =
    stateDocFeeFlag &&
    maxMarkup >= 1 &&
    STATES_WITH_NO_DOC_FEE.includes(state) &&
    bankSlug !== TtNewBankEnum.FifthThird;

  return canApplyMinMarkup ? 1 : null;
};

export const getDefaultBankByDealType = (dealType = DealType.Buyout): TtNewBankEnum =>
  DEFAULT_BANKS_BY_DEAL_TYPE[dealType];

type GetCorrectDocFeeInput = {
  lienholder: Maybe<LienholderNameEnum>;
  state: string;
  stateDocFeeFlag: boolean;
};

// This function is duplicate of the one in API - utils/financialInfos.ts
export const getCorrectDocFee = ({ lienholder, state, stateDocFeeFlag }: GetCorrectDocFeeInput) => {
  const stateHasNoDocFee = STATES_WITH_NO_DOC_FEE.includes(state);
  const shouldRemoveDocFee =
    lienholder === LienholderNameEnum.ALLY || (stateDocFeeFlag && stateHasNoDocFee);

  // If we don't want to apply the fee, then we multiply by 0 to remove it.
  return DOC_FEE * (shouldRemoveDocFee ? 0 : 1);
};
